/**
 * ProviderIcon — renders a small logo for each AI source provider.
 *
 * Usage:
 *   <ProviderIcon providerId="anthropic" className="h-4 w-4" />
 *
 * Falls back to a generic icon for unknown providers.
 *
 * @module apps/web/src/ui/ProviderIcon
 */

interface Props {
  /**
   * Optional: a catalog model routed through the default upstream carries no
   * sourceProviderId, and an icon must never be the reason a page fails to
   * render. Anything unrecognised falls through to the generic mark.
   */
  providerId?: string | null;
  className?: string;
}

export function ProviderIcon({ providerId, className = 'h-4 w-4' }: Props) {
  const id = (providerId ?? '').toLowerCase();
  switch (id) {
    case 'openrouter':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" opacity="0.4"/>
          <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" opacity="0.6"/>
          <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      );
    case 'anthropic':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.827 3.52h3.602L24 20.48h-3.602l-6.571-16.96zM8.262 3.52h3.602L18.435 20.48h-3.602L8.262 3.52zM0 20.48L6.571 3.52h3.602L3.602 20.48H0z"/>
        </svg>
      );
    case 'openai':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.364a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.408 9.224V6.892a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.076.076 0 0 1-.038-.052V6.075a4.5 4.5 0 0 1 7.375-3.46l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v3l-2.597 1.5-2.607-1.5z"/>
        </svg>
      );
    case 'deepseek':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-1-13h2v10h-2V7zm-3 3h2v7H8v-7zm6 0h2v7h-2v-7z"/>
        </svg>
      );
    case 'z-ai':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm3 3h8v2H10l6 6h-8v-2h6L8 8z"/>
        </svg>
      );
    case 'x-ai':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3h4.5l3 4.5L14 3h3l-5 7 5.5 8H14l-3.5-5L7 18H4l5.5-8L3 3zm2 2l7 10 7-10h-1.5L12 12 6.5 5H5z"/>
        </svg>
      );
    case 'mistral':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3h4v4H3V3zm6 0h4v4H9V3zm6 0h3v4h-3V3zM3 9h4v4H3V9zm6 0h4v4H9V9zm6 0h3v4h-3V9zM3 15h4v4H3v-4zm6 0h4v4H9v-4zm6 0h3v4h-3v-4z"/>
        </svg>
      );
    case 'moonshot':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-2-12a3 3 0 0 0-3 3v4a3 3 0 0 0 6 0v-4a3 3 0 0 0-3-3zm1 7a1 1 0 0 1-2 0v-4a1 1 0 0 1 2 0v4z"/>
        </svg>
      );
    case 'google':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4a6 6 0 1 1 0 12 6 6 0 0 1 0-12z" fill="#4285F4"/>
          <path d="M12 6a6 6 0 0 1 6 6h4a10 10 0 0 0-10-10v4z" fill="#EA4335"/>
          <path d="M6 12a6 6 0 0 1 6-6V2a10 10 0 0 0-10 10h4z" fill="#FBBC05"/>
          <path d="M12 18a6 6 0 0 1-6-6H2a10 10 0 0 0 10 10v-4z" fill="#34A853"/>
        </svg>
      );
    default:
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9"/>
          <path d="M12 7v5l3 3"/>
        </svg>
      );
  }
}
